import React, { useEffect, useState, useCallback } from 'react';
import i18next from 'i18next';
import { dataService } from '../../services/DataService';
import type { Video } from '../../types/Video';
import type { Document } from '../../types/Document';
import type { Link } from '../../types/Link';

interface UseModuleElearningContentResult {
  contentFn: () => {
    playlist: Array<Record<string, any>>;
    docs: Array<React.ReactElement>;
    links: Array<React.ReactElement>;
  };
  isContentLoading: boolean;
  error: string | null;
}

export function useModuleElearningContent(moduleId?: string | undefined): UseModuleElearningContentResult {
  const [videos, setVideos] = useState<Video[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [links, setLinks] = useState<Link[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!moduleId) return;
      setIsLoading(true);
      setError(null);
      try {
        const [videosRes, docsRes, linksRes] = await Promise.all([
          dataService.getItems(`modules/${moduleId}/videos`),
          dataService.getItems(`modules/${moduleId}/documents`),
          dataService.getItems(`modules/${moduleId}/links`),
        ]);

        if (isMounted) {
          if (videosRes.statusCode === 200) {
            setVideos(videosRes.body.map((v: any) => ({ ...v, id: v.id || v.docId || v.documentId })).sort((a: Video, b: Video) => a.order - b.order));
          }
          if (docsRes.statusCode === 200) {
            setDocuments(docsRes.body.map((d: any) => ({ ...d, id: d.id || d.docId || d.documentId })).sort((a: Document, b: Document) => a.order - b.order));
          }
          if (linksRes.statusCode === 200) {
            setLinks(linksRes.body.map((l: any) => ({ ...l, id: l.id || l.docId || l.documentId })).sort((a: Link, b: Link) => a.order - b.order));
          }
        }
      } catch (e: any) {
        if (isMounted) setError(e?.message || 'Failed to load elearning content');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [moduleId]);

  const contentFn = useCallback(() => {
    const lang = i18next.language || 'de';
    // Map videos to playlist items expected by existing UI
    const getLangValue = (de: string, fr?: string, it?: string) => {
      if (lang === 'fr') return fr || de;
      if (lang === 'it') return it || de;
      return de;
    };

    const playlist = videos.map((v) => ({
      id: getLangValue(v.videoId, v.videoId_fr, v.videoId_it),
      title: getLangValue(v.title, v.title_fr, v.title_it),
    }));

    const docs = documents.map((d) => {
      const url = getLangValue(d.storageUrl, d.storageUrl_fr, d.storageUrl_it);
      const title = getLangValue(d.title, d.title_fr, d.title_it);
      return (
        <a href={url} target="_blank" rel="noreferrer" key={d.id}>
          {title}
        </a>
      );
    });

    const linksElements = links.map((l) => {
      const url = getLangValue(l.url, l.url_fr, l.url_it);
      const title = getLangValue(l.title, l.title_fr, l.title_it);
      return (
        <a href={url} target="_blank" rel="noreferrer" key={l.id}>
          {title}
        </a>
      );
    });

    return {
      playlist,
      docs,
      links: linksElements,
    };
  }, [videos, documents, links]);

  return {
    contentFn,
    isContentLoading: isLoading,
    error,
  };
}

export default useModuleElearningContent;
